/**
 * ActivityHeatmap Component
 * Main container component for the GitHub-style activity heatmap
 * Shows daily activity from challenges and practice tests
 *
 * Can be used in two modes:
 * 1. Self-fetching: No props - fetches data for current user
 * 2. Data-driven: Pass activities and stats props (for public profiles)
 */

import { Calendar, Flame } from 'lucide-react'
import HeatmapGrid from './HeatmapGrid'
import HeatmapLegend from './HeatmapLegend'
import { useActivityHeatmap } from './useActivityHeatmap'
import LoadingSpinner from '../LoadingSpinner'

/**
 * Check if dark mode is active
 * Uses the document class to detect dark mode
 */
function useDarkMode() {
  // Check if document has dark class
  if (typeof document !== 'undefined') {
    return document.documentElement.classList.contains('dark')
  }
  return false
}

/**
 * ActivityHeatmap - Main heatmap card component
 * Displays a GitHub-style contribution heatmap showing daily activity
 *
 * @param {Object} props - Component props
 * @param {Object} props.activities - Optional pre-fetched activity data (keyed by date)
 * @param {Object} props.stats - Optional pre-fetched stats { totalActiveDays, maxDailyActivity, currentStreak }
 * @param {number} props.currentStreak - Optional current streak (can also be in stats)
 */
function ActivityHeatmap({ activities: propActivities, stats: propStats, currentStreak: propStreak, className = 'sm:w-fit' }) {
  // Only fetch if no data provided
  const shouldFetch = !propActivities && !propStats
  const { activities: fetchedActivities, stats: fetchedStats, loading, error } = useActivityHeatmap({
    autoFetch: shouldFetch
  })

  const isDark = useDarkMode()

  // Use props if provided, otherwise use fetched data
  const activities = propActivities || fetchedActivities
  const stats = propStats || fetchedStats
  const currentStreak = propStreak ?? stats.currentStreak ?? 0

  if (shouldFetch && loading) {
    return (
      <div className={`card ${className}`}>
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Activity</h3>
          </div>
        </div>
        <div className="card-body">
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="sm" />
            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading activity...</span>
          </div>
        </div>
      </div>
    )
  }

  if (shouldFetch && error) {
    return (
      <div className={`card ${className}`}>
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Activity</h3>
          </div>
        </div>
        <div className="card-body">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Unable to load activity data
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`card ${className}`} style={{ overflow: 'visible' }}>
      {/* Card Header - stacks on mobile */}
      <div className="card-header">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-600 flex-shrink-0" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Activity</h3>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {stats.totalActiveDays || 0} active {stats.totalActiveDays === 1 ? 'day' : 'days'}
            </span>
            {currentStreak > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-full">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-700 dark:text-orange-400">
                  {currentStreak} day streak
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card Body - scrollable on mobile */}
      <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="card-body space-y-4" style={{ minWidth: '680px' }}>
          <div className="flex flex-col items-center">
            {/* Heatmap Grid */}
            <HeatmapGrid
              activities={activities}
              maxDailyActivity={stats.maxDailyActivity || 0}
              isDark={isDark}
            />

            {/* Legend */}
            <div className="flex justify-end w-full max-w-[654px] mt-4">
              <HeatmapLegend isDark={isDark} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ActivityHeatmap
